/**
 * Dernier changement appliqué au plan (côté serveur) avec son annulation en un clic, toujours visible tant qu'il existe (maquettes L06-03 et L06-15).
 * Ne confondre avec « Annuler le dernier changement » du bandeau de brouillon, qui n'agit que sur les modifications pas encore appliquées.
 */
import React, { useState } from 'react';
import { Undo2 } from 'lucide-react';
import { PorteeChangement } from './types.ts';

export const LIBELLE_PORTEE: Record<PorteeChangement, string> = { maintenant: 'à partir de maintenant', service: 'pour le service', permanent: 'permanent' };

interface Props {
  description: string;
  portee: PorteeChangement;
  /** Droit de modifier le plan. */
  peutAnnuler: boolean;
  /** Un brouillon est en cours : on l'applique ou on le réinitialise d'abord, sinon il porterait sur un plan qui vient de changer. */
  brouillonEnCours: boolean;
  onAnnuler: () => Promise<void>;
}

export const BandeauDernierChangement: React.FC<Props> = ({ description, portee, peutAnnuler, brouillonEnCours, onAnnuler }) => {
  const [enCours, setEnCours] = useState<boolean>(false);
  const [erreur, setErreur] = useState<string>('');
  const annuler = async () => {
    setEnCours(true); setErreur('');
    try { await onAnnuler(); } catch (e) { setErreur(e instanceof Error && e.message ? e.message : 'Le changement n’a pas pu être annulé.'); } finally { setEnCours(false); }
  };
  return (
    <div className="px-3 sm:px-4 py-1.5 border-b border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/60 flex flex-wrap items-center justify-between gap-x-2 gap-y-1 text-xs">
      <p className="text-slate-600 dark:text-slate-300 min-w-0 flex-1 truncate" title={description}>
        <span className="font-semibold text-slate-800 dark:text-slate-100">Dernier changement</span> ({LIBELLE_PORTEE[portee]}) : <span>{description}</span>
      </p>
      {peutAnnuler && (
        <button
          type="button"
          onClick={() => void annuler()}
          disabled={enCours || brouillonEnCours}
          title={brouillonEnCours ? 'Appliquez ou réinitialisez d’abord le brouillon en cours.' : undefined}
          className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-md font-semibold border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer min-h-[32px]"
        >
          <Undo2 className="w-3.5 h-3.5" aria-hidden="true" />
          <span>{enCours ? 'Annulation…' : 'Annuler ce changement'}</span>
        </button>
      )}
      {erreur && <p role="alert" className="basis-full text-red-700 dark:text-red-300 font-semibold">{erreur}</p>}
    </div>
  );
};
